/**
 * The one template of every Security Advisory (DEV-138).
 *
 * Every vulnerability case renders its advisory — and with it the customer
 * mail — from this template. Administrators edit it in the Markdown editor
 * (formatted or plain, placeholders highlighted). The placeholders are a fixed
 * set, an unknown one is rejected (mirrored here so saving is disabled with
 * the reason). A change applies at the next display of every advisory; queued
 * and sent mails keep their text.
 */

import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useMemo, useState, type ReactNode, type SubmitEvent } from 'react';

import Banner from '../../components/Banner';
import MarkdownEditor from '../../components/markdown/MarkdownEditor';
import Spinner from '../../components/Spinner';
import { errorMessage, fetchAdvisoryTemplate, resetAdvisoryTemplate, saveAdvisoryTemplate } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import type { AdvisorySource, AdvisoryTemplateSettings } from '../../lib/types';
import { fillTemplate, placeholderNames } from '../triage/caseAdvisoryModel';
import MailPreview from '../triage/MailPreview';
import { markdownToHtml } from '../triage/mailText';

import './advisory-template.css';

const MAX_TEMPLATE_LENGTH = 50_000;

const SOURCE_LABELS: Readonly<Record<AdvisorySource, MessageDescriptor>> = {
  fact: msg`Kernangabe des Falls`,
  cmdb: msg`CMDB, im Advisory überschreibbar`,
  table: msg`Tabelle aus dem Fall`,
  release: msg`Veröffentlichung`,
  field: msg`Zusatzfeld im Advisory`,
  notes: msg`Weitere Hinweise (optional)`,
};


export function AdvisoryTemplateCard(): ReactNode {
  // The template and its preview are document content and stay as written; only the UI around them is translated.
  const { t } = useLingui();
  const [settings, setSettings] = useState<AdvisoryTemplateSettings | null>(null);
  const [markdown, setMarkdown] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchAdvisoryTemplate()
      .then((next) => {
        setSettings(next);
        setMarkdown(next.markdown);
      })
      .catch((err: unknown) => setError(errorMessage(err)));
  }, []);

  const known = useMemo(() => Object.fromEntries((settings?.placeholders ?? []).map((p) => [p.name, p])), [settings]);
  const examples = useMemo(() => Object.fromEntries((settings?.placeholders ?? []).map((p) => [p.name, p.example])), [settings]);
  /** Why the edited template cannot be saved, or null. */
  function templateProblem(known: ReadonlySet<string>): string | null {
    if (!markdown.trim()) return t`Die Vorlage darf nicht leer sein.`;
    if (markdown.length > MAX_TEMPLATE_LENGTH) return t`Die Vorlage ist zu lang.`;
    const unknown = placeholderNames(markdown).filter((name) => !known.has(name));
    if (unknown.length === 0) return null;
    const names = unknown.map((name) => `{{${name}}}`).join(', ');
    return t`Unbekannte Platzhalter: ${names}`;
  }

  const problem = settings ? templateProblem(new Set(Object.keys(known))) : null;
  const changed = settings !== null && markdown.trim() !== settings.markdown.trim();
  const placeholderCount = settings?.placeholders.length ?? 0;
  const updatedAt = fmtDate(settings?.updated_at);
  const updatedBy = settings?.updated_by ?? t`unbekannt`;
  const preview = useMemo(() => fillTemplate(markdown, examples, known).text, [markdown, examples, known]);

  async function apply(action: () => Promise<AdvisoryTemplateSettings>, message: string): Promise<void> {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const next = await action();
      setSettings(next);
      setMarkdown(next.markdown);
      setSaved(message);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function save(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (problem || !changed || busy) return;
    void apply(() => saveAdvisoryTemplate(markdown), t`Vorlage gespeichert. Sie gilt ab der nächsten Anzeige jedes Advisorys.`);
  }

  function reset(): void {
    if (!window.confirm(t`Die angepasste Vorlage verwerfen und die Standardvorlage verwenden?`)) return;
    void apply(resetAdvisoryTemplate, t`Standardvorlage wiederhergestellt.`);
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2><Trans>Security Advisory – Vorlage</Trans></h2>
      </div>
      <div className="card-body">
        {error !== null ? <Banner kind="error">{error}</Banner> : null}
        {saved !== null ? <Banner kind="ok">{saved}</Banner> : null}
        {settings === null && error === null ? <Spinner label={t`Wird geladen…`} /> : null}
        {settings !== null ? (
          <form onSubmit={save}>
            <p className="hint">
              <Trans>
                Eine Vorlage für alle Security Advisories und damit für den Text der Kundenmails. Die Platzhalter füllt
                die Anwendung aus dem Schwachstellenfall; eingereihte und versendete Mails bleiben unverändert.
              </Trans>{' '}
              {settings.is_default ? t`Derzeit gilt die Standardvorlage.` : t`Angepasst ${updatedAt} · ${updatedBy}.`}
            </p>
            <div className="field">
              <label id="advisory-template-label" htmlFor="advisory-template"><Trans>Vorlage</Trans></label>
              <MarkdownEditor
                id="advisory-template"
                labelledBy="advisory-template-label"
                describedBy="advisory-template-hint"
                rows={18}
                value={markdown}
                maxLength={MAX_TEMPLATE_LENGTH}
                disabled={busy}
                onChange={(next) => {
                  setSaved(null);
                  setMarkdown(next);
                }}
              />
              <span id="advisory-template-hint" className={problem ? 'err' : 'hint'}>
                {problem ?? t`Die erste Überschrift wird zum Betreff der Kundenmail.`}
              </span>
            </div>
            <details className="advisory-template-section">
              <summary><Trans>Verfügbare Platzhalter ({placeholderCount})</Trans></summary>
              <dl className="detail-list">
                {settings.placeholders.map((placeholder) => (
                  <PlaceholderRow key={placeholder.name} {...placeholder} />
                ))}
              </dl>
            </details>
            <details className="advisory-template-section">
              <summary><Trans>Vorschau mit Beispielwerten</Trans></summary>
              <MailPreview html={markdownToHtml(preview)} title={t`Vorschau der Advisory-Vorlage`} />
            </details>
            <div className="ops-dt-actions">
              <button type="button" className="ghost" disabled={busy || settings.is_default} onClick={reset}>
                <Trans>Auf Standard zurücksetzen</Trans>
              </button>
              <button type="submit" className="primary" disabled={busy || !changed || problem !== null}>
                {busy ? <Spinner variant="on-accent" label={t`Wird gespeichert…`} /> : t`Speichern`}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
}

function PlaceholderRow({ name, label, source }: Readonly<{ name: string; label: string; source: AdvisorySource }>): ReactNode {
  const { i18n } = useLingui();
  return (
    <>
      <dt className="mono">{`{{${name}}}`}</dt>
      <dd>{label} <span className="muted">· {i18n._(SOURCE_LABELS[source])}</span></dd>
    </>
  );
}

export default AdvisoryTemplateCard;
